// A run's Units (Mitch, 2026-11-04: "I don't know why on the runs under plan, it has all of the units for the current
// plan split up into their own modules. I think that would be fine under a single module like in the Set aside section,
// but for all build items"): every variant the run makes in one table, a band per product when it makes more than one:
// units planned (a past run's built, typed in), then what happened to them: ordered, built, received and good (a past
// run's set aside and kept). Under it the sub-assemblies it built, who supplies each part for this batch, and an
// opening balance for a run that started before HHL Ops. What it all cost is the Landed cost tab.

import { useState } from "react";
import { numberOf } from "../../../../../shared/calc.ts";
import { type RunUnits, runResult } from "../../../../../shared/costs.ts";
import type { ProductionRunLine } from "../../../../../shared/db.gen.ts";
import { runPresales } from "../../../../../shared/presales.ts";
import { openingOf, runPlanEditable } from "../../../../../shared/runs.ts";
import { shortDate, useCostIndex } from "../../../lib/costs.ts";
import { qty } from "../../../lib/format.ts";
import { can, mutate } from "../../../lib/mutate.ts";
import { usePresaleIndex } from "../../../lib/presales.ts";
import { setSearch } from "../../../lib/router.tsx";
import { Button, cx, Input, Select } from "../../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../../ui/DataTable.tsx";
import { toast } from "../../../ui/toast.tsx";
import { Why } from "../../../ui/Why.tsx";
import { Card } from "../Workspace.tsx";
import { BomPicker, OpeningDialog, WhoSupplies } from "./RunPlan.tsx";
import type { RunProps } from "./RunWorkspace.tsx";

type UnitRow = { id: string; sku: string; name: string; band: TableSection | null; line: ProductionRunLine | undefined; u: RunUnits | undefined };

/** A whole number of units, 0 or more (empty is 0), as typed in a cell; null (with a toast) when it isn't one. */
function unitsOf(text: string): number | null {
  if (text.trim() === "") return 0;
  const n = numberOf(text);
  if (Number.isInteger(n) && n >= 0) return n;
  toast.error("Units are whole numbers, 0 or more");
  return null;
}

export function UnitsTab(props: RunProps) {
  const { run, scope } = props;
  const cx_ = useCostIndex();
  const res = runResult(cx_, run.id);
  const rp = runPresales(usePresaleIndex(), run.id);
  const lines = cx_.linesByRun.get(run.id) ?? [];
  const lineOf = new Map(lines.map((l) => [l.item_id, l]));
  const past = run.historical;
  const editable = runPlanEditable(run) && can("production_run_line.update");
  const [same, setSame] = useState("");
  const [recording, setRecording] = useState(false);
  const opening = openingOf(run);
  const setUnits = (itemId: string, value: number) => {
    const l = lineOf.get(itemId);
    if (l) {
      if (Number(l.qty) !== value) mutate("production_run_line.update", { id: l.id, patch: { qty: value } });
    } else if (value > 0)
      mutate("production_run_line.create", { id: crypto.randomUUID(), run_id: run.id, item_id: itemId, qty: value, position: lines.length });
  };
  const setAside = (l: ProductionRunLine, n: number) => {
    if (n !== Number(l.set_aside)) mutate("production_run_line.update", { id: l.id, patch: { set_aside: n } });
  };
  // What the run could build: every variant of the project's products (a band per product when there's more than one),
  // the kits and sub-assemblies it builds on their own, and any line for an item that isn't one of them any more. Only
  // what's in the run shows (units typed, or ordered, built or received, or just added); the rest is behind Add.
  const kits = scope.families.filter((f) => f.kind === "subassembly" && f.via === "top" && f.bom.item_id);
  const bands = scope.products.length + (kits.length ? 1 : 0);
  const many = bands > 1;
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
  const rowOf = (id: string, fallbackSku: string, band: TableSection | null): UnitRow => {
    const item = cx_.itemById.get(id);
    return {
      id,
      sku: item?.sku ?? fallbackSku,
      name: item?.shopify_variant_title || item?.name || fallbackSku,
      band,
      line: lineOf.get(id),
      u: res?.units.get(id),
    };
  };
  const candidates: UnitRow[] = [
    ...scope.products.flatMap((p, order) => p.variants.map((v) => rowOf(v.id, v.sku, many ? { key: p.key, order, label: p.title } : null))),
    ...kits.map((f) => rowOf(f.bom.item_id as string, f.label, many ? { key: "kits", order: scope.products.length, label: "Kits and sub-assemblies" } : null)),
  ];
  for (const l of lines)
    if (!candidates.some((r) => r.id === l.item_id))
      candidates.push(rowOf(l.item_id, "", many ? { key: "other", order: bands, label: "Not in this project's products" } : null));
  const inRun = (r: UnitRow) => Number(r.line?.qty ?? 0) > 0 || !!(r.u?.ordered || r.u?.built || r.u?.received) || added.has(r.id);
  // A run with nothing in it yet lists everything to fill in; once it has units, only what's in it
  const fresh = !candidates.some((r) => Number(r.line?.qty ?? 0) > 0 || r.u?.ordered || r.u?.built || r.u?.received);
  const rows = fresh ? candidates : candidates.filter(inRun);
  const notIn = fresh ? [] : candidates.filter((r) => !inRun(r));
  const add = (id: string) => {
    if (!id) return;
    if (!lineOf.has(id)) mutate("production_run_line.create", { id: crypto.randomUUID(), run_id: run.id, item_id: id, qty: 0, position: lines.length });
    setAdded((a) => new Set([...a, id]));
  };
  const canAdd = (editable || (past && can("production_run_line.update"))) && notIn.length > 0;
  const built = lines.filter((l) => Number(l.qty) > 0);
  const anyBuilt = [...(res?.units.values() ?? [])].some((u) => u.built > 0);
  const sumOf = (f: (r: UnitRow) => number) => (rs: UnitRow[]) => <span className="font-mono text-[12px]">{qty(rs.reduce((n, r) => n + f(r), 0))}</span>;
  const count = (key: "ordered" | "built" | "received" | "rejected" | "scrapped", header: string, extra: Partial<Column<UnitRow>> = {}): Column<UnitRow> => ({
    key,
    header,
    width: "84px",
    align: "right",
    value: (r) => r.u?.[key] ?? 0,
    cell: (r) => (r.u?.[key] ? <span className="font-mono text-[12px]">{qty(r.u[key])}</span> : <span className="text-faint">—</span>),
    total: sumOf((r) => r.u?.[key] ?? 0),
    ...extra,
  });
  const good = (r: UnitRow) => r.u?.good ?? Number(r.line?.qty ?? 0) - Number(r.line?.set_aside ?? 0);
  const columns: Column<UnitRow>[] = [
    { key: "sku", header: "SKU", width: "minmax(110px,140px)", cell: (r) => <span className="truncate font-mono text-[12px]">{r.sku}</span> },
    { key: "name", header: "Name", width: "minmax(160px,1fr)", cell: (r) => <span className="truncate">{r.name}</span> },
    {
      key: "units",
      header: past ? "Built" : "Units",
      headerTitle: editable ? "Click a number to change it; Enter saves it and goes on to the next" : past ? undefined : "How many this run makes",
      width: "90px",
      align: "right",
      value: (r) => Number(r.line?.qty ?? 0),
      cell: (r) =>
        Number(r.line?.qty ?? 0) ? (
          <span className="font-mono text-[12px] font-medium">{qty(Number(r.line?.qty))}</span>
        ) : (
          <span className="text-faint">{editable ? "0" : "—"}</span>
        ),
      edit: editable
        ? {
            get: (r) => (Number(r.line?.qty ?? 0) ? String(Number(r.line?.qty)) : ""),
            save: (r, text) => {
              const n = unitsOf(text);
              if (n != null) setUnits(r.id, n);
            },
          }
        : undefined,
      total: sumOf((r) => Number(r.line?.qty ?? 0)),
    },
    ...(past
      ? [
          {
            key: "set_aside",
            header: "Set aside",
            headerTitle: "Units built that weren't yours to sell: kept by the CM for its own store, samples given away",
            width: "90px",
            align: "right",
            value: (r) => Number(r.line?.set_aside ?? 0),
            cell: (r) =>
              Number(r.line?.set_aside ?? 0) ? (
                <span className="font-mono text-[12px]">{qty(Number(r.line?.set_aside))}</span>
              ) : (
                <span className="text-faint">0</span>
              ),
            edit: can("production_run_line.update")
              ? {
                  get: (r) => (Number(r.line?.set_aside ?? 0) ? String(Number(r.line?.set_aside)) : ""),
                  save: (r, text) => {
                    const n = unitsOf(text);
                    if (n != null && r.line) setAside(r.line, n);
                  },
                  can: (r) => Number(r.line?.qty ?? 0) > 0,
                }
              : undefined,
            total: sumOf((r) => Number(r.line?.set_aside ?? 0)),
          } satisfies Column<UnitRow>,
        ]
      : [
          count("ordered", "Ordered", { headerTitle: "On the make PO" }),
          count("built", "Built", { headerTitle: "What the CM reported built" }),
          count("received", "Received", { headerTitle: "Arrived at HHL" }),
          count("rejected", "Rejected", { optional: true, headerTitle: "Rejected at receiving" }),
          count("scrapped", "Scrapped", { optional: true, headerTitle: "Finished units scrapped at the CM" }),
        ]),
    {
      key: "good",
      header: past ? "Kept" : "Good",
      headerTitle: past ? "Built less set aside: the units you sell" : "Built less rejected and scrapped: the units you sell",
      width: "90px",
      align: "right",
      value: good,
      cell: (r) =>
        r.u ? (
          <span className="font-mono text-[12px] font-medium">
            <Why
              title={past ? "Kept" : "Good units"}
              lines={[
                { label: past ? "Built" : "Built (CM reports)", value: qty(r.u.built) },
                ...(r.u.setAside ? [{ label: "Set aside (not yours to sell)", value: `−${qty(r.u.setAside)}` }] : []),
                ...(past ? [] : [{ label: "Rejected at receiving", value: `−${qty(r.u.rejected)}` }]),
                ...(past ? [] : [{ label: "Finished units scrapped at the CM", value: `−${qty(r.u.scrapped)}` }]),
                { label: past ? "Kept" : "Good", value: qty(r.u.good), strong: true },
              ]}
              note={r.u.estimate ? "Nothing built yet: the units planned stand in" : r.u.notBuilt ? "Never built: the run closed without it" : undefined}
            >
              {r.u.estimate ? <span className="text-faint">{qty(r.u.good)}*</span> : qty(r.u.good)}
            </Why>
          </span>
        ) : (
          <span className="text-faint">—</span>
        ),
      total: sumOf((r) => (r.u && !r.u.estimate ? r.u.good : 0)),
    },
  ];
  const applySame = () => {
    const n = unitsOf(same);
    if (n == null) return;
    for (const l of built) setAside(l, n);
    setSame("");
  };
  const subs = [...(res?.units ?? [])].filter(([id, u]) => cx_.itemById.get(id)?.kind !== "sell" && (u.planned || u.ordered || u.built));
  return (
    <>
      {opening ? (
        <div className="rounded-lg border border-accent/40 bg-accent-soft/40 px-4 py-2.5 text-[12px]">
          <span className="font-semibold">Opening balance as of {shortDate(opening.as_of)}</span>
          <span className="text-muted">
            {" "}
            · recorded {shortDate(opening.recorded_at)}: {opening.lines.reduce((n, l) => n + l.built, 0)} built before then
            {opening.lines.some((l) => l.received) ? `, ${opening.lines.reduce((n, l) => n + l.received, 0)} already received at HHL` : ""}
            {opening.parts.length ? `, ${opening.parts.length} parts counted at the CM` : ""}.
          </span>
        </div>
      ) : (
        can("production_run.record_opening") &&
        !past &&
        !anyBuilt &&
        (cx_.posByRun.get(run.id) ?? []).some((p) => p.kind === "make" && p.status !== "draft") && (
          <div className="flex items-center gap-3 rounded-lg border border-dashed border-line-strong bg-surface px-4 py-2.5 text-[12px]">
            <span className="text-muted">
              Started before HHL Ops? Record what had already happened (built, shipped, received, parts at the CM) as of a date.
            </span>
            <Button size="sm" className="ml-auto" onClick={() => setRecording(true)}>
              Record opening balance
            </Button>
          </div>
        )
      )}
      <Card
        boxed={false}
        title="Units"
        note={
          past
            ? "What this past run built, per variant: click a number to type it. Set aside: any the CM kept or you gave away; their cost goes on the units kept."
            : editable
              ? "How many of each this run makes: click a number to type it. The rest fills in as the CM reports and goods arrive."
              : `Units are fixed while ${run.status.replace("_", " ")} (move back to Ordering to change them)`
        }
      >
        <DataTable
          fit={24}
          rows={rows}
          columns={columns}
          sectionOf={many ? (r) => r.band : undefined}
          editing={editable || (past && can("production_run_line.update"))}
          exportName="Run units"
          pageFilters={false}
          rowNoun={["variant", "variants"]}
          bar={{
            // Each product's BOM revision for this run
            filters: scope.products.map((p) => (
              <BomPicker
                key={p.key}
                run={run}
                variants={p.variants}
                productKey={p.key}
                label={scope.products.length > 1 ? p.title : undefined}
                disabled={!runPlanEditable(run) || !can("production_run_line.update")}
              />
            )),
            actions: canAdd && (
              <span className="w-56">
                <Select
                  aria-label="Add a variant to this run"
                  value=""
                  onChange={(e) => add(e.target.value)}
                  options={[
                    { value: "", label: `Add to this run (${notIn.length} not in it)` },
                    ...notIn.map((r) => ({ value: r.id, label: `${r.sku} · ${r.name}${r.band ? ` (${r.band.label})` : ""}` })),
                  ]}
                  className="h-7 text-[12px]"
                />
              </span>
            ),
          }}
          empty={
            candidates.length
              ? "Nothing in this run yet: add what it builds with Add to this run, then type its units."
              : "This project has no products yet: link them in Configure → Products."
          }
        />
        {past && (run.set_aside_note || (can("production_run_line.update") && built.length > 1)) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line px-4 py-2 text-[11px] text-faint">
            {run.set_aside_note && <span>Set aside: {run.set_aside_note}</span>}
            {can("production_run_line.update") && built.length > 1 && (
              <span className="ml-auto flex items-center gap-1.5 whitespace-nowrap">
                Set aside the same of each
                <span className="w-16 shrink-0">
                  <Input
                    aria-label="Set aside the same number of each"
                    value={same}
                    placeholder="50"
                    inputMode="numeric"
                    onChange={(e) => setSame(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && applySame()}
                    className="h-7 text-right font-mono text-[12px]"
                  />
                </span>
                <Button size="sm" disabled={!same.trim()} onClick={applySame}>
                  Set
                </Button>
              </span>
            )}
          </div>
        )}
        {!past && rows.some((r) => r.u?.estimate && Number(r.line?.qty ?? 0) > 0) && (
          <div className="border-t border-line px-4 py-2 text-[11px] text-faint">* nothing built yet, so the units planned stand in</div>
        )}
      </Card>
      {subs.length > 0 && <SubUnits subs={subs} />}
      <WhoSupplies run={run} />
      {!rp.presales.length && !rp.holds.length && can("presale.create") && !past && (
        <div className="px-1 text-[12px] text-muted">
          Selling some of these before they're made (a Kickstarter, pre-orders)?{" "}
          <button type="button" onClick={() => setSearch({ rt: "presales" })} className="text-accent-text hover:underline">
            Set up a pre-sale
          </button>
        </div>
      )}
      {recording && <OpeningDialog run={run} onClose={() => setRecording(false)} />}
    </>
  );
}

type SubRow = { id: string; sku: string; name: string; u: RunUnits };

/** Sub-assemblies the run builds for its variants (main PCBAs), kept at the CM for the builds above. */
function SubUnits({ subs }: { subs: [string, RunUnits][] }) {
  const cx_ = useCostIndex();
  const rows: SubRow[] = subs.map(([id, u]) => ({ id, sku: cx_.itemById.get(id)?.sku ?? "", name: cx_.itemById.get(id)?.name ?? "", u }));
  const n = (k: "ordered" | "built" | "scrapped", header: string): Column<SubRow> => ({
    key: k,
    header,
    width: "84px",
    align: "right",
    value: (r) => r.u[k],
    cell: (r) => <span className={cx("font-mono text-[12px]", !r.u[k] && "text-faint")}>{qty(r.u[k])}</span>,
  });
  const columns: Column<SubRow>[] = [
    { key: "sku", header: "SKU", width: "minmax(110px,150px)", cell: (r) => <span className="truncate font-mono text-[12px]">{r.sku}</span> },
    { key: "name", header: "Name", width: "minmax(160px,1fr)", cell: (r) => <span className="truncate">{r.name}</span> },
    n("ordered", "Ordered"),
    n("built", "Built"),
    n("scrapped", "Scrapped"),
  ];
  return (
    <Card boxed={false} title="Sub-assemblies it builds" note="Built at the CM for the variants above; their cost is inside the variants'">
      <DataTable fit rows={rows} columns={columns} exportName="Run sub-assemblies" pageFilters={false} rowNoun={["item", "items"]} />
    </Card>
  );
}
